import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/components/email/EmParts";

const TONE = {
  working: "bg-emerald-50 text-emerald-700 border-emerald-200", waiting_for_data: "bg-slate-50 text-slate-600 border-slate-200",
  missing_config: "bg-amber-50 text-amber-800 border-amber-200", error: "bg-rose-50 text-rose-700 border-rose-200",
  ai_suggestion: "bg-indigo-50 text-indigo-700 border-indigo-200", handled_elsewhere: "bg-slate-50 text-slate-500 border-slate-200",
};
const ORDER = ["error", "missing_config", "working", "waiting_for_data", "ai_suggestion", "handled_elsewhere"];

export function AutoHealth() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const load = useCallback(async (refresh) => {
    setBusy(true);
    try { setD((await api.get("/email-marketing/automations/health", { params: { refresh } })).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  }, []);
  useEffect(() => { load(false); }, [load]);
  if (!d) { return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Checking automations…</div>; }
  const rows = d.items.filter((x) => !filter || x.health === filter).sort((a, b) => ORDER.indexOf(a.health) - ORDER.indexOf(b.health));
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 text-sm" data-testid="auto-health">
      <div className="flex flex-wrap items-center gap-2"><h2 className="text-base font-bold text-slate-900 mr-auto">Automation health</h2>
        <span className="text-xs text-slate-400">Checked {d.computed_at?.slice(0, 16).replace("T", " ")}</span>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => load(true)} data-testid="auto-health-refresh">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</Button></div>
      <p className="text-slate-500">"Waiting for operational data" is not a fault: the automation works and will start finding customers as EPOS, stock, installations, servicing and equipment records are used. Only "Error" needs correcting.</p>
      {d.general.length > 0 && <p className="text-amber-800 text-xs" data-testid="auto-health-general">Setup: {d.general.join(" · ")}</p>}
      <div className="flex flex-wrap gap-2">{ORDER.map((k) => (
        <button key={k} type="button" onClick={() => setFilter(filter === k ? "" : k)} className={`rounded-full border px-3 py-1 text-xs font-medium ${TONE[k]} ${filter === k ? "ring-2 ring-slate-300" : ""}`} data-testid={`auto-health-chip-${k}`}>
          {d.labels[k]} · {d.counts[k]}</button>))}</div>
      <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">{rows.map((x) => (
        <div key={x.id} className="py-2 flex flex-wrap gap-2 items-start" data-testid={`auto-health-row-${x.id}`}>
          <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold shrink-0 ${TONE[x.health]}`}>{x.label}</span>
          <span className="flex-1 min-w-[220px]"><b>{x.name}</b> <span className="text-xs text-slate-400">· {x.mode.toUpperCase()}</span><span className="block text-xs text-slate-500">{x.detail}</span></span>
        </div>))}</div>
    </section>
  );
}
